import { ReactNode, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
  actionButton: {
    background: '#F5F5F5',
    borderRadius: '8px',
    fontSize: '14px',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '16px',
    marginBottom: '16px',
    color: '#1C2541',
  },
  actionButtonContainer: {
    marginBottom: '40px',
  },
  popper: {
    zIndex: 1000,
  },
  menuItemRoot: {
    whiteSpace: 'unset',
    wordBreak: 'break-word',
    '&:hover': {
      backgroundColor: '#FFF',
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#FFF',
    },
  },
  paper: {
    maxHeight: '500px',
    overflow: 'auto',
  },
}));

type DropdownMenuItem = {
  content: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

type Props = {
  title: string;
  startIcon: ReactNode;
  menuItems: DropdownMenuItem[];
};

const EstateBalanceSheetDropdownMenu = ({ title, startIcon, menuItems }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleItemClick = (event: Event | React.SyntheticEvent, item: DropdownMenuItem) => {
    handleClose(event);
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <>
      <Button
        className={classes.actionButton}
        startIcon={startIcon}
        ref={anchorRef}
        id="composition-button"
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        {title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        className={classes.popper}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >
                  {menuItems.map((item, i) => (
                    <MenuItem
                      onClick={(evt) => handleItemClick(evt, item)}
                      key={i}
                      classes={{ root: classes.menuItemRoot }}
                      disabled={item.disabled}
                      disableRipple
                    >
                      {item.content}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default EstateBalanceSheetDropdownMenu;
